import React, { FC } from 'react';

import { config, yup } from 'data';
import { paymentService } from 'services';
import { useForm, useLang, useMutation } from 'hooks';
import { Alert, Form, Input, Modal } from 'components/ui';
import { Nullish } from 'types/common';
import { ModalBaseProps } from 'types/components';
import { PaymentCardOtpVerificationParams } from 'types/services';

type FormValues = PaymentCardOtpVerificationParams;

const initialValues: FormValues = {
  otpCode: '',
};

const validationSchema = yup.object({
  otpCode: yup.string().required().trim().length(config.PAYMENT_OTP_LENGTH),
});

type OtpVerificationModalProps = ModalBaseProps & {
  trxRef: string;
  error?: Nullish<string>;
  pending?: boolean;
  onSuccess?: VoidFunction;
};

const OtpVerificationModal: FC<OtpVerificationModalProps> = ({
  trxRef,
  error,
  pending,
  onSuccess,
  ...props
}) => {
  const form = useForm<FormValues>();
  const lang = useLang();

  const verifyCardPaymentOtpMutation = useMutation({
    mutationFn: (values: FormValues) => paymentService.verifyCardPaymentOtp(trxRef, values),
    onSuccess,
  });

  const handleSubmit = async (values: FormValues) => {
    await verifyCardPaymentOtpMutation.mutateAsync(values);

    return false;
  };

  const loading = pending || verifyCardPaymentOtpMutation.isPending;

  return (
    <Modal
      title={lang.get('invoicePayment.otpModal.title')}
      caption={lang.get('invoicePayment.otpModal.caption')}
      okText={lang.get('common.actions.continue')}
      okButtonProps={{ block: true }}
      cancelButtonProps={{ hidden: true }}
      width="small"
      confirmLoading={loading}
      closable={false}
      maskClosable={false}
      onOk={form.submit}
      {...props}
    >
      <Form
        form={form}
        initialValues={initialValues}
        validationSchema={validationSchema}
        disabled={loading}
        onFinish={handleSubmit}
      >
        <Form.Item hidden={!error}>
          <Alert type="error" message={error} />
        </Form.Item>
        <Form.Item name="otpCode">
          <Input
            placeholder={lang.get('common.form.otp.placeholder')}
            minLength={config.PAYMENT_OTP_LENGTH}
            maxLength={config.PAYMENT_OTP_LENGTH}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default OtpVerificationModal;
